const COMMON_API = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/common`

const ENDPOINTS = {
    CALLBACK: `${COMMON_API}/callback`,
    PENDING_CALLBACK: `${COMMON_API}/pending-callback`,
}

export const CALLBACK_TYPES = {
    CALLBACK: 10,
    ANONYMOUS_CALLBACK: 11,
    QUOTE: 20,
    DETAILED_QUOTE: 21,
    SUBSCRIPTION: 30,
    CUSTOM: 40,
} as const

export type CallbackType = typeof CALLBACK_TYPES[keyof typeof CALLBACK_TYPES]

export interface ICallbackData {
    first_name: string
    last_name: string
    phone: string
    email?: string
    appointment_date: string | null
    appointment_time: string | null
    product_type: string
    token: string
    type: CallbackType
    offer_id?: string
    offer_cost?: number | null
    from_link: string
    accept_phone_contact?: boolean
    accept_email_contact?: boolean
}

export interface IMercerCallback {
    commercial_name: string
    commercial_phone: string
}

export interface ICallbackResponse {
    mercer_callback?: IMercerCallback
}

/** The customer's upcoming callback slot on a loan quote; dates are naive Paris local time ("YYYY-MM-DDTHH:mm:ss"). */
export interface IPendingCallback {
    taskId: number | null
    dueDate: string
    endDate: string | null
    phone: string | null
    displayedNumber: string | null
}

const toPendingCallback = (data: any): IPendingCallback | null =>
    data?.pending === true && typeof data.due_date === 'string'
        ? {
              taskId: data.task_id ?? null,
              dueDate: data.due_date,
              endDate: data.end_date ?? null,
              phone: data.phone ?? null,
              displayedNumber: data.displayed_number ?? null,
          }
        : null

const CALLBACK_ALREADY_PLANNED_CODE = 'CALLBACK_ALREADY_PLANNED'
const NO_PENDING_CALLBACK_CODE = 'NO_PENDING_CALLBACK'

/** The back refused the request (HTTP 409): the loan quote already has an upcoming callback. */
export class CallbackAlreadyPlannedError extends Error {
    constructor(public readonly pending: IPendingCallback | null) {
        super(CALLBACK_ALREADY_PLANNED_CODE)
        this.name = 'CallbackAlreadyPlannedError'
        // instanceof survives the ES5 class downlevel of Error subclasses
        Object.setPrototypeOf(this, new.target.prototype)
    }
}

/** The slot to change is gone: handled by the expert, past, or already cancelled. */
export class NoPendingCallbackError extends Error {
    constructor() {
        super(NO_PENDING_CALLBACK_CODE)
        this.name = 'NoPendingCallbackError'
        Object.setPrototypeOf(this, new.target.prototype)
    }
}

export async function throwIfCallbackAlreadyPlanned(response: Response): Promise<void> {
    if (response.status !== 409) return
    const body = await response.clone().json().catch(() => null)
    if (body?.code === CALLBACK_ALREADY_PLANNED_CODE) {
        throw new CallbackAlreadyPlannedError(toPendingCallback(body))
    }
}

const pendingCallbackUrl = (token: string) => `${ENDPOINTS.PENDING_CALLBACK}?product=loan&token=${encodeURIComponent(token)}`

export async function getPendingLoanCallback(token: string): Promise<IPendingCallback | null> {
    try {
        const response = await fetch(pendingCallbackUrl(token), { cache: 'no-store' })
        if (!response.ok) return null
        return toPendingCallback(await response.json())
    } catch {
        // the back still refuses a duplicate on submit: a failed check must not lock the funnel
        return null
    }
}

export async function rescheduleLoanCallback(
    token: string,
    appointmentDate: string,
    appointmentTime: string,
    phone?: string
): Promise<IPendingCallback> {
    const response = await fetch(ENDPOINTS.PENDING_CALLBACK, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            product: 'loan',
            token,
            appointment_date: appointmentDate,
            appointment_time: appointmentTime,
            ...(phone ? { phone } : {}),
        }),
        cache: 'no-store',
    })
    const body = await response.json().catch(() => null)
    if (response.status === 404 && body?.code === NO_PENDING_CALLBACK_CODE) {
        throw new NoPendingCallbackError()
    }
    const pending = response.ok ? toPendingCallback(body) : null
    if (!pending) {
        throw new Error(`Error rescheduling callback: ${response.statusText}`)
    }
    return pending
}

export async function cancelLoanCallback(token: string): Promise<void> {
    const response = await fetch(pendingCallbackUrl(token), { method: 'DELETE', cache: 'no-store' })
    // 404 means the slot is already gone, which is the outcome the customer asked for
    if (!response.ok && response.status !== 404) {
        throw new Error(`Error cancelling callback: ${response.statusText}`)
    }
}

export async function postCallback(data: ICallbackData): Promise<ICallbackResponse> {
    const response = await fetch(
        ENDPOINTS.CALLBACK,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
            cache: 'no-store',
        }
    )

    await throwIfCallbackAlreadyPlanned(response)

    if (!response.ok) {
        throw new Error(`Error posting callback: ${response.statusText}`)
    }

    return await response.json()
}
